import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { detectLocation, geoErrorText, geoLabel, saveGeo, useGeo, getGeo } from "@/lib/geo";
import { toast } from "sonner";
import { MapPin, X, Plus, Check, ChevronRight, House, Loader2, Crosshair, HeartHandshake, Wrench } from "lucide-react";

export const ACTIVE_HOME_KEY = "homiie_active_home";

export function getActiveHomeId() {
    return localStorage.getItem(ACTIVE_HOME_KEY) || "";
}

export function LocationSheet({ accent = "services", onClose, onPick }) {
    const nav = useNavigate();
    const coral = accent === "care";
    const brand = coral ? "#F43F5E" : "#009C95";
    const [homes, setHomes] = useState(null);
    const [active, setActive] = useState(getActiveHomeId());
    const [enabledIds, setEnabledIds] = useState([]);
    const [locating, setLocating] = useState(false);
    const geo = useGeo();

    useEffect(() => {
        api.get("/homes").then((r) => setHomes(r.data || [])).catch(() => setHomes([]));
        api.get("/service-areas").then((r) => setEnabledIds((r.data || []).map((a) => a.id))).catch(() => {});
    }, []);

    const detect = async () => {
        setLocating(true);
        try {
            const g = await detectLocation();
            toast.success(g.services_available ? `Services available in ${geoLabel(g)}` : `${geoLabel(g)}: Services coming soon. Care is available.`);
        } catch (e) { toast.error(geoErrorText(e)); }
        finally { setLocating(false); }
    };

    const pick = (h) => {
        localStorage.setItem(ACTIVE_HOME_KEY, h.id);
        setActive(h.id);
        saveGeo({ locality: h.label, city: h.city, pincode: h.pincode, lat: h.lat, lng: h.lng, home_id: h.id,
            area: h.area_id ? { id: h.area_id, name: h.area_name } : null,
            services_available: enabledIds.includes(h.area_id), care_available: !!h.area_id || /^342/.test(h.pincode || "") });
        api.post("/location/resolve", { pincode: h.pincode || undefined, lat: h.lat ?? undefined, lng: h.lng ?? undefined })
            .then((r) => saveGeo({ ...getGeo(), care_available: r.data.care_available, care_city: r.data.care_city })).catch(() => {});
        onPick && onPick(h);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-navy/50" onClick={onClose}>
            <div className="max-h-[85vh] w-full max-w-md overflow-y-auto rounded-t-3xl bg-white p-6 pb-8 homiie-scroll" onClick={(e) => e.stopPropagation()} data-testid="location-sheet">
                <div className="mb-1 flex items-center justify-between">
                    <div className="flex items-center gap-2"><MapPin size={18} style={{ color: brand }} /><h3 className="font-heading text-lg font-extrabold text-navy">Service location</h3></div>
                    <button data-testid="location-sheet-close" onClick={onClose} className="rounded-full bg-navy/5 p-1.5"><X size={16} /></button>
                </div>
                <p className="text-sm text-navy/55">Choose which address to use for bookings.</p>

                <button data-testid="location-detect-btn" onClick={detect} disabled={locating} className="mt-4 flex w-full items-center gap-3 rounded-2xl border p-3.5 text-left transition-colors disabled:opacity-60" style={{ borderColor: brand }}>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl" style={{ backgroundColor: `${brand}1a`, color: brand }}>{locating ? <Loader2 size={17} className="animate-spin" /> : <Crosshair size={17} />}</span>
                    <div className="flex-1 overflow-hidden">
                        <p className="font-heading text-sm font-bold" style={{ color: brand }}>{locating ? "Detecting your location…" : "Use my current location"}</p>
                        {geo && <p className="truncate text-xs text-navy/50" data-testid="location-current-label">{geoLabel(geo)}{geo.pincode ? ` · ${geo.pincode}` : ""}</p>}
                    </div>
                </button>
                {geo && (
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="location-availability">
                        <span className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${geo.services_available ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}><Wrench size={11} /> {geo.services_available ? "Services available" : "Services coming soon"}</span>
                        <span className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold ${geo.care_available ? "bg-rose-50 text-rose-600" : "bg-slate-100 text-slate-500"}`}><HeartHandshake size={11} /> {geo.care_available ? "Care available" : `Care: ${geo.care_city || "Jodhpur"} only`}</span>
                    </div>
                )}
                {homes === null && <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" style={{ color: brand }} /></div>}
                {homes && homes.length === 0 && (
                    <div className="mt-5 rounded-2xl border border-dashed border-navy/15 p-5 text-center">
                        <p className="text-sm text-navy/55">No saved addresses yet.</p>
                    </div>
                )}

                <div className="mt-4 space-y-2.5">
                    {homes?.map((h) => {
                        const sel = active === h.id || (!active && homes[0]?.id === h.id);
                        return (
                            <button key={h.id} data-testid={`location-home-${h.id}`} onClick={() => pick(h)} className="flex w-full items-center gap-3 rounded-2xl border p-3.5 text-left transition-colors" style={sel ? { borderColor: brand, backgroundColor: `${brand}0d` } : { borderColor: "rgba(9,35,59,0.1)" }}>
                                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl" style={{ backgroundColor: `${brand}1a`, color: brand }}><House size={17} /></span>
                                <div className="flex-1 overflow-hidden">
                                    <div className="flex items-center gap-2"><p className="truncate font-heading text-sm font-bold text-navy">{h.label}</p>{h.property_type && <span className="shrink-0 rounded-full bg-navy/5 px-2 py-0.5 text-[10px] font-bold text-navy/55">{h.property_type}</span>}</div>
                                    <p className="truncate text-xs text-navy/50">{h.address_line}{h.area_name ? ` · ${h.area_name}` : ""}</p>
                                </div>
                                {sel && <Check size={18} style={{ color: brand }} />}
                            </button>
                        );
                    })}
                </div>

                <button data-testid="location-add-address" onClick={() => { onClose(); nav("/my-home?add=1"); }} className="mt-4 flex w-full items-center justify-center gap-2 rounded-full py-3 text-sm font-bold text-white" style={{ backgroundColor: brand }}><Plus size={16} /> Add a new address</button>
                <button data-testid="location-manage-homes" onClick={() => { onClose(); nav("/my-home"); }} className="mt-2 flex w-full items-center justify-center gap-1 rounded-full py-2.5 text-sm font-semibold text-navy/60">Manage My Homes <ChevronRight size={15} /></button>
            </div>
        </div>
    );
}
